import { useEffect, useState } from "react";
import { DatabaseBackup, Download, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

export default function Backups() {
  const [data, setData] = useState({ backups: [], job: null });
  const [restoreFor, setRestoreFor] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/backups").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!["running", "restoring"].includes(data.job?.status)) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [data.job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const runNow = async () => {
    setBusy(true);
    try {
      await api.post("/admin/backups/run");
      toast.success("Backup started");
      setTimeout(load, 1000);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const download = async (key) => {
    try {
      const r = await api.get(`/admin/backups/download?key=${encodeURIComponent(key)}`);
      window.open(r.data.url, "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const restore = async (key, collections) => {
    if (!window.confirm(`Restore ${collections ? collections.length + " collection(s)" : "EVERYTHING"} from this backup? Live collections are swapped only after the staged copy verifies; the previous live copy is preserved.`)) return;
    try {
      await api.post("/admin/backups/restore", { key, collections, confirm: "RESTORE" });
      toast.success("Restore started — staging first, swap on success");
      setRestoreFor(null);
      setTimeout(load, 1000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const job = data.job || {};

  return (
    <div className="space-y-5" data-testid="backups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Backups & Data Safety</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Nightly at 3:30am IST to the R2 bucket (<span className="font-mono">backups/</span>), plus a manual copy before anything risky.
            Retention: 30 recent + first-of-month for 12 months. The audit log is included in every backup.
          </p>
        </div>
        <Button onClick={runNow} disabled={busy || ["running", "restoring"].includes(job.status)} className="bg-stone-900 text-white" data-testid="backup-now-btn">
          <DatabaseBackup size={15} className="mr-2" /> Back up now
        </Button>
      </div>

      {job.status && (
        <p className={`text-xs font-mono px-3 py-2 border ${job.status.includes("fail") ? "border-red-300 bg-red-50 text-red-700" : "border-neutral-200 bg-white text-neutral-600"}`} data-testid="backup-job-status">
          Last job: {job.status}{job.key ? ` — ${job.key}` : ""}{job.total_records != null ? ` — ${job.total_records} records` : ""}{job.error ? ` — ${job.error}` : ""}
        </p>
      )}

      <section className="bg-white border border-neutral-200 p-5" data-testid="deploy-checklist">
        <h2 className="font-heading font-semibold mb-2">Before every deploy — 2-minute checklist</h2>
        <ol className="text-sm text-neutral-600 list-decimal list-inside space-y-1">
          <li>Take a manual backup here ("Back up now") and wait for it to finish.</li>
          <li>Check the preview link thoroughly — place a test COD order, open a product page.</li>
          <li>Never deploy during a campaign, sale or a metal-rate spike day.</li>
          <li>If anything looks wrong after deploy: use Rollback (chat input) to return to the last checkpoint — it is free and instant.</li>
          <li>If data got mangled: restore the affected collection only, from the list below.</li>
        </ol>
      </section>

      <section className="bg-white border border-neutral-200 p-5" data-testid="backup-list">
        <h2 className="font-heading font-semibold mb-3">Backups</h2>
        {data.backups.map((b) => (
          <div key={b.id || b._id} className="border-b border-neutral-100 py-3" data-testid={`backup-${b.key}`}>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-mono">{new Date(b.at).toLocaleString("en-IN")}</span>
              <span className="text-[10px] font-mono uppercase border px-1.5 py-0.5 border-neutral-200">{b.kind}</span>
              <span className="text-neutral-500">{fmtSize(b.size_bytes)}</span>
              <span className="text-neutral-500">{b.collection_count} collections · {b.total_records} records</span>
              <div className="ml-auto flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => download(b.key)} data-testid={`backup-download-${b.key}`}><Download size={13} className="mr-1" /> Download</Button>
                <Button variant="outline" size="sm" onClick={() => { setRestoreFor(restoreFor === b.key ? null : b.key); setPicked({}); }} data-testid={`backup-restore-${b.key}`}>
                  <RotateCcw size={13} className="mr-1" /> Restore
                </Button>
              </div>
            </div>
            {restoreFor === b.key && (
              <div className="mt-2 ml-2 border-l-2 border-amber-300 pl-4 py-2 space-y-2" data-testid="restore-panel">
                <p className="text-xs text-neutral-500">Pick collections (common case: just <b>products</b>), or restore everything:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(b.counts || {}).map((c) => (
                    <label key={c} className="text-xs flex items-center gap-1 border border-neutral-200 px-2 py-1">
                      <input type="checkbox" checked={!!picked[c]} onChange={(e) => setPicked((p) => ({ ...p, [c]: e.target.checked }))} data-testid={`restore-col-${c}`} />
                      {c} <span className="text-neutral-400">({b.counts[c]})</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={!Object.values(picked).some(Boolean)}
                    onClick={() => restore(b.key, Object.keys(picked).filter((k) => picked[k]))} data-testid="restore-selected-btn">
                    Restore selected
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200"
                    onClick={() => restore(b.key, null)} data-testid="restore-all-btn">
                    Restore everything
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {!data.backups.length && <p className="text-sm text-neutral-400">No backups yet — the first nightly run lands at 3:30am IST, or take one now.</p>}
      </section>
    </div>
  );
}
